import { useState, useEffect } from 'react';
import api from '../../services/api';

interface Task {
  id: number;
  subject: string;
  chapter: string;
  activityType: string;
  estimatedTime: number;
  priority: string;
  status: string;
  completionPercentage: number;
}

interface StudyPlan {
  id: number;
  startDate: string;
  endDate: string;
  totalDays: number;
  completionPercentage: number;
  stats: {
    totalTasks: number;
    completedTasks: number;
    pendingTasks: number;
    completionRate: number;
  };
}

export default function StudyPlanPage() {
  const [loading, setLoading] = useState(true);
  const [hasPreferences, setHasPreferences] = useState(false);
  const [hasPlan, setHasPlan] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<StudyPlan | null>(null);
  const [todayTasks, setTodayTasks] = useState<Task[]>([]);
  const [todaySummary, setTodaySummary] = useState<any>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Onboarding form state
  const [formData, setFormData] = useState({
    targetExam: 'both',
    attemptYear: new Date().getFullYear() + 1,
    dailyStudyHours: 4,
    currentClass: '12',
    subjectConfidence: {
      physics: 50,
      chemistry: 50,
      mathematics: 50
    }
  });

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      // Check if user has preferences
      const prefsRes = await api.get('/study-plan/preferences');
      const hasPrefs = !!prefsRes.data.data;
      setHasPreferences(hasPrefs);

      if (hasPrefs) {
        // Check if user has active plan
        const planRes = await api.get('/study-plan/current');
        const plan = planRes.data.data;
        
        if (plan) {
          setHasPlan(true);
          setCurrentPlan(plan);
          
          // Get today's tasks
          const tasksRes = await api.get('/study-plan/today');
          setTodayTasks(tasksRes.data.data.tasks);
          setTodaySummary(tasksRes.data.data.summary);
        }
      }
    } catch (error) {
      console.error('Error checking status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreferences = async (e: React.FormEvent) => {
    e.preventDefault();
    setGenerating(true);

    try {
      // Save preferences
      await api.post('/study-plan/preferences', formData);

      // Generate plan
      await api.post('/study-plan/generate');

      // Refresh status
      await checkStatus();
      setShowOnboarding(false);
    } catch (error: any) {
      console.error('Error:', error);
      alert(error.response?.data?.error || 'Failed to generate plan');
    } finally {
      setGenerating(false);
    }
  };

  const handleCompleteTask = async (taskId: number) => {
    try {
      await api.post(`/study-plan/task/${taskId}/complete`, {});
      await checkStatus();
    } catch (error) {
      console.error('Error completing task:', error);
    }
  };

  const getActivityIcon = (type: string) => {
    const icons: Record<string, string> = {
      concept_learning: '📖',
      smart_practice: '✍️',
      revision: '🔄',
      mock_test: '📝',
      sectional_test: '📋',
      formula_revision: '📐'
    };
    return icons[type] || '📚';
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      high: 'bg-red-50 border-red-500 text-red-700',
      medium: 'bg-yellow-50 border-yellow-500 text-yellow-700',
      low: 'bg-blue-50 border-blue-500 text-blue-700'
    };
    return colors[priority] || 'bg-gray-50 border-gray-500 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your study plan...</p>
        </div>
      </div>
    );
  }

  // Onboarding Flow
  if (!hasPreferences || showOnboarding) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Your Personalized Study Plan</h1>
          <p className="text-gray-600 mb-8">Let's build a study plan tailored just for you! 🎯</p>

          <form onSubmit={handleSavePreferences} className="space-y-6">
            {/* Target Exam */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Exam
              </label>
              <select
                value={formData.targetExam}
                onChange={(e) => setFormData({ ...formData, targetExam: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="jee_main">JEE Main</option>
                <option value="jee_advanced">JEE Advanced</option>
                <option value="both">Both</option>
              </select>
            </div>

            {/* Attempt Year */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attempt Year
              </label>
              <input
                type="number"
                value={formData.attemptYear}
                onChange={(e) => setFormData({ ...formData, attemptYear: parseInt(e.target.value) })}
                min={new Date().getFullYear()}
                max={new Date().getFullYear() + 3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Daily Study Hours */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Daily Study Hours: {formData.dailyStudyHours} hours
              </label>
              <input
                type="range"
                min="2"
                max="12"
                step="0.5"
                value={formData.dailyStudyHours}
                onChange={(e) => setFormData({ ...formData, dailyStudyHours: parseFloat(e.target.value) })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>2 hrs</span>
                <span>12 hrs</span>
              </div>
            </div>

            {/* Current Class */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Class
              </label>
              <select
                value={formData.currentClass}
                onChange={(e) => setFormData({ ...formData, currentClass: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="11">Class 11</option>
                <option value="12">Class 12</option>
                <option value="dropper">Dropper</option>
              </select>
            </div>

            {/* Subject Confidence */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Subject Confidence (0-100)
              </label>
              
              <div className="space-y-4">
                {['physics', 'chemistry', 'mathematics'].map((subject) => (
                  <div key={subject}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm capitalize">{subject}</span>
                      <span className="text-sm font-medium">{formData.subjectConfidence[subject as keyof typeof formData.subjectConfidence]}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={formData.subjectConfidence[subject as keyof typeof formData.subjectConfidence]}
                      onChange={(e) => setFormData({
                        ...formData,
                        subjectConfidence: {
                          ...formData.subjectConfidence,
                          [subject]: parseInt(e.target.value)
                        }
                      })}
                      className="w-full"
                    />
                  </div>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={generating}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {generating ? 'Generating Your Plan... 🎯' : 'Generate My Study Plan 🚀'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // No Plan Yet
  if (!hasPlan) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">📚</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to Start Your Journey?</h2>
          <p className="text-gray-600 mb-6">Generate your personalized study plan now!</p>
          <button
            onClick={async () => {
              setGenerating(true);
              try {
                await api.post('/study-plan/generate');
                await checkStatus();
              } catch (error: any) {
                alert(error.response?.data?.error || 'Failed to generate plan');
              } finally {
                setGenerating(false);
              }
            }}
            disabled={generating}
            className="bg-blue-600 text-white py-3 px-8 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400"
          >
            {generating ? 'Generating...' : 'Generate Study Plan'}
          </button>
        </div>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">My Study Plan</h1>
        <p className="text-gray-600 mt-2">Stay consistent and achieve your goals! 🎯</p>
      </div>

      {/* Progress Overview */}
      {currentPlan && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-1">Plan Progress</div>
            <div className="text-2xl font-bold text-blue-600">{parseFloat(String(currentPlan.completionPercentage)).toFixed(1)}%</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-1">Total Tasks</div>
            <div className="text-2xl font-bold text-gray-900">{currentPlan.stats.totalTasks}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-1">Completed</div>
            <div className="text-2xl font-bold text-green-600">{currentPlan.stats.completedTasks}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-1">Days Remaining</div>
            <div className="text-2xl font-bold text-orange-600">{currentPlan.totalDays}</div>
          </div>
        </div>
      )}

      {/* Today's Tasks */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Today's Tasks</h2>
          {todaySummary && (
            <div className="text-sm text-gray-600">
              {todaySummary.completedTasks}/{todaySummary.totalTasks} completed
            </div>
          )}
        </div>

        {todayTasks.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p className="text-4xl mb-4">🎉</p>
            <p>No tasks for today! Take a well-deserved break.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {todayTasks.map((task) => (
              <div
                key={task.id}
                className={`border-l-4 rounded-lg p-4 ${getPriorityColor(task.priority)}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-2xl">{getActivityIcon(task.activityType)}</span>
                      <div>
                        <h3 className="font-semibold capitalize">{task.subject} - {task.chapter}</h3>
                        <p className="text-sm text-gray-600 capitalize">
                          {task.activityType.replace('_', ' ')} • {task.estimatedTime} min
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {task.status === 'completed' ? (
                    <div className="flex items-center space-x-2 text-green-600">
                      <span>✓</span>
                      <span className="text-sm font-medium">Completed</span>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleCompleteTask(task.id)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
                    >
                      Mark Complete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Motivation */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg shadow p-6 text-white text-center">
        <p className="text-lg font-medium">
          "Success is the sum of small efforts repeated day in and day out." 💪
        </p>
        <p className="text-sm mt-2 opacity-90">Keep going! You're doing great!</p>
      </div>
    </div>
  );
}
